import { FormProvider } from 'react-hook-form'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/view/components/ui/dialog'
import { BasicInput } from '@/view/components/BasicInput'
import { BasicSelect } from '@/view/components/BasicSelect'
import { SegmentedControl } from '@/view/components/SegmentedControl'
import { Button } from '@/view/components/ui/button'
import { ConfirmationTransactionModal } from '@/view/pages/WithdrawSimulation/components/NewWithdrawModal/components/ConfirmationTransactionModal'
import { cpfMask } from '@/app/utils/cpfFormater'
import { zipCodeMask } from '@/app/utils/zipCodeFormater'
import { phoneMask } from '@/app/utils/phoneFormater'
import useGenerateProposalModal from './useGenerateProposalModal'

interface GenerateProposalModalProps {
  onContinue: () => void
  onBack: () => void
}

export default function GenerateProposalModal({
  onContinue,
  onBack,
}: GenerateProposalModalProps) {
  const {
    form,
    selected,
    setSelected,
    isCityDisabled,
    occupationOptions,
    submitModal,
    isLoading,
    isOpenConfirmModal,
    setIsOpenConfirmModal,
    pin,
    setPin,
    handleOpenConfirmation,
    user,
    cityOptions,
    setCityInput,
    cepWarning,
  } = useGenerateProposalModal({
    onContinue,
  })

  const {
    formState: { errors },
  } = form

  return (
    <DialogContent className="sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle className="text-xl font-semibold text-black01">
          Vamos simular seu crédito
        </DialogTitle>
        <DialogDescription className="!mt-0 text-base text-black04">
          Preencha os dados abaixo para verificar a disponibilidade
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submitModal}>
        <FormProvider {...form}>
          <div className="mb-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <BasicInput
              name="cpf"
              label="CPF"
              error={errors.cpf?.message}
              onInput={cpfMask}
              maxLength={14}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <BasicInput
              name="name"
              label="Nome completo"
              error={errors.name?.message}
            />
            <BasicInput
              name="phone"
              label="Telefone"
              error={errors.phone?.message}
              onInput={phoneMask}
            />
            <BasicInput
              name="birthDate"
              type="date"
              label="Data de nascimento"
              error={errors.birthDate?.message}
            />
            <BasicSelect
              name="ocupation"
              label="Ocupação"
              options={occupationOptions}
              placeholder="Selecione uma opção"
              error={errors.ocupation?.message}
            />
            <div className="mt-2 sm:col-span-2">
              <SegmentedControl
                value={selected}
                onChange={setSelected}
                options={[
                  { label: 'CEP', value: 'cep' },
                  { label: 'Cidade - UF', value: 'city' },
                ]}
              />
            </div>

            {selected === 'cep' && (
              <div className="flex items-start gap-6 sm:col-span-2">
                <div className="w-1/2">
                  <BasicInput
                    name="zipcode"
                    placeholder="00000-000"
                    error={errors.zipcode?.message || cepWarning}
                    onInput={zipCodeMask}
                    maxLength={9}
                  />
                </div>

                <div className="mt-2 flex h-[38px] w-1/2 items-center">
                  {cepWarning ? (
                    <span className="text-sm text-red-500">
                      CEP não encontrado
                    </span>
                  ) : (
                    form.watch('city') || ' '
                  )}
                </div>
              </div>
            )}

            {selected === 'city' && (
              <div className="sm:col-span-2">
                {selected === 'city' && (
                  <div className="sm:col-span-2">
                    <BasicSelect
                      name="city"
                      options={cityOptions}
                      placeholder="Selecione a UF - Cidade"
                      disabled={isCityDisabled}
                      onInputChange={(value) => setCityInput(value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="w-[160px] rounded-full text-primary"
            >
              Voltar
            </Button>
            <Button
              type={user?.isPinProtected ? 'button' : 'submit'}
              onClick={
                user?.isPinProtected ? handleOpenConfirmation : undefined
              }
              className="w-[160px] rounded-full font-semibold text-white"
              isLoading={isLoading}
            >
              Continuar
            </Button>
          </div>
        </FormProvider>
      </form>

      <ConfirmationTransactionModal
        clientName={form.watch('name') || ''}
        isOpen={isOpenConfirmModal}
        onClose={setIsOpenConfirmModal}
        pin={pin}
        setPin={setPin}
        submitForm={submitModal}
        user={user}
      />
    </DialogContent>
  )
}
